/** The Selection tab's details: the selected (or clicked) unit, or else the
 *  hex under the cursor or clicked. Rebuilt when what's shown changes (a
 *  frame, a new unit or hex), not every animation frame. */

import { GoodIcon } from "./RecipePicker";
import { BLOCKED, cancelSites, siteName, sites } from "./BuildTab";
import { sideName } from "../state/players";
import { For, Show, createSignal, type JSX } from "solid-js";
import { DepotKind, NO_ID, TERRAIN_NAMES, UnitFlags, type Header } from "../replay/format";
import type { Pick, Renderer } from "../render/renderer";
import type { WorldState } from "../state/world";
import { bar, int } from "../ui/format";
import { industry } from "../state/industry";
import { mineWorks, structureName } from "../state/structures";
import { stationInfo } from "../state/stations";
import { cellOf, minutes, sampling } from "../state/sampling";
import { ORE_HUES, oreLabel, oreRGB } from "../render/colour";
import { isUnpowered, powerOf } from "../state/designs";

export interface SelectionActions {
  select(id: number | null): void;
  follow(): void;
  center(id: number): void;
  following(): boolean;
  /** A commander's side (null watching or in a replay), and taking one of
   *  their structures down. */
  commander(): number | null;
  decommission(at: [number, number]): void;
}

/** Steel each structure kind cost (construction.rs: work over
 *  WORK_PER_STEEL): decommissioning refunds three quarters. */
const BUILT_STEEL: Partial<Record<DepotKind, number>> = {
  [DepotKind.Depot]: 100,
  [DepotKind.Station]: 150,
  [DepotKind.Mine]: 200,
  [DepotKind.Plant]: 300,
  [DepotKind.Barracks]: 150,
};

/** The structure (by hex) whose Decommission is armed: kept outside the
 *  tab, which is rebuilt every frame. */
const [armedAt, setArmedAt] = createSignal<string | null>(null);
let armedTimer = 0;

/** Decommission, asked once in place: the first click arms it ("Confirm"),
 *  a second within a few seconds sends it. */
function Decommission(p: { at: string; refund: number; run: () => void }): JSX.Element {
  const armed = () => armedAt() === p.at;
  return (
    <button
      class="decommission"
      classList={{ armed: armed() }}
      onClick={() => {
        clearTimeout(armedTimer);
        if (armed()) {
          setArmedAt(null);
          p.run();
          return;
        }
        setArmedAt(p.at);
        armedTimer = window.setTimeout(() => setArmedAt(null), 4000);
      }}
    >
      {armed() ? `Confirm: +${p.refund} steel in 30 s` : "Decommission"}
    </button>
  );
}

export type Shown = { kind: "unit"; id: number; frame: number } | { kind: "tile"; pick: Pick; frame: number } | null;

interface Props {
  header: Header;
  state: WorldState;
  renderer: Renderer;
  actions: SelectionActions;
  shown: () => Shown;
}

type Row = [string, JSX.Element];

function Rows(props: { rows: Row[] }): JSX.Element {
  return (
    <dl class="kv">
      <For each={props.rows}>
        {([k, v]) => (
          <>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </>
        )}
      </For>
    </dl>
  );
}

export function SelectionTab(p: Props): JSX.Element {
  return (
    <Show when={p.shown()} keyed>
      {(v) => (v.kind === "unit" ? <UnitView {...p} id={v.id} /> : <TileView {...p} pick={v.pick} />)}
    </Show>
  );
}

function UnitView(p: Props & { id: number }): JSX.Element {
  const { header: h, state: s, actions: a, id } = p;
  const d = h.designs[s.design[id]];
  const f = h.factions[s.faction[id]];
  const head = (
    <div class="head">
      <span class="swatch" style={{ background: f?.color }} />
      <span class="name">{d?.name ?? "Unit"}</span>
      <span class="muted">{f?.name ?? ""}</span>
    </div>
  );
  if (!s.alive[id]) {
    return (
      <>
        <div class="inspector-title">Unit</div>
        {head}
        <p class="muted">Not on the field now</p>
        <div class="actions">
          <button onClick={() => a.select(null)}>Deselect</button>
        </div>
      </>
    );
  }
  const tags: [string, string][] = [];
  if (d.damage > 0 && s.ammo[id] === 0) tags.push(["Out of ammo", "bad"]);
  if (s.suppression[id] >= 5) tags.push(["Suppressed", "warn"]);
  if (s.dug[id] >= 12) tags.push(["Dug in", ""]);
  if (s.flags[id] & UnitFlags.CONVOY) tags.push([s.flags[id] & UnitFlags.RETURNING ? "Convoy, returning" : "Convoy, delivering", ""]);
  if (d.indirect) tags.push(["Indirect fire", ""]);
  if (d.name === "Prisoners") tags.push(["Prisoner", "warn"]);
  if (s.hp[id] < d.hp * 0.35) tags.push(["Wounded", "bad"]);
  if (s.flags[id] & UnitFlags.ENGINE_OUT) tags.push(["Engine knocked out", "bad"]);
  if (s.flags[id] & UnitFlags.WEAPON_OUT) tags.push(["Gun knocked out", "bad"]);
  const rows: Row[] = [["Health", bar(s.hp[id] / d.hp, "#4fbf6a", `${s.hp[id]} / ${d.hp}`)]];
  if (d.ammo > 0) rows.push(["Ammo", bar(s.ammo[id] / d.ammo, "#d9b44a", `${s.ammo[id]} / ${d.ammo}`)]);
  if (d.cargo > 0) rows.push(["Cargo", bar(s.cargo[id] / d.cargo, "#c9c2a5", `${s.cargo[id]} / ${d.cargo}`)]);
  rows.push(["Suppression", bar(s.suppression[id] / 15, "#e0604f", `${Math.round((s.suppression[id] / 15) * 100)}%`)]);
  rows.push(["Dug in", bar(s.dug[id] / 15, "#a07c4a", `${Math.round((s.dug[id] / 15) * 100)}%`)]);
  const t = d.indirect ? NO_ID : s.target[id];
  if (t !== NO_ID) rows.push(["Target", <a onClick={() => a.select(t)}>{`${h.designs[s.design[t]]?.name ?? "unit"} #${t}`}</a>]);
  else if (d.damage > 0 && !d.indirect) rows.push(["Target", "none"]);
  // A builder's job: the construction site it stands at, how far along.
  if (d.modules?.some((m) => m.kind === "construction")) {
    const [q, r] = [s.q(s.tile[id]), s.r(s.tile[id])];
    const near = (at: [number, number]) => {
      const dq = at[0] - q;
      const dr = at[1] - r;
      return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr)) <= 1;
    };
    const site = sites().find((x) => near(x.at));
    rows.push(["Job", site ? bar(site.done / Math.max(1, site.need), "#c4512f", `Building ${site.what}`) : sites().length ? "On the way to a site" : "Idle (no sites)"]);
  }
  // A prospector's survey: sampling where it stands (how far, how long
  // left), surveying on the move, or idle.
  if (d.modules?.some((m) => m.kind === "prospector")) {
    const [q, r] = [s.q(s.tile[id]), s.r(s.tile[id])];
    const smp = sampling().find((x) => x.cell === cellOf(q, r));
    const moving = s.prevTile[id] !== s.tile[id];
    rows.push([
      "Survey",
      smp && !moving
        ? bar(smp.done, "#c9c2a5", `Sampling ${smp.kind === "Soil" ? "soil" : "a deep core"}: ${Math.floor(smp.done * 100)}%, ${minutes(smp.left)} left`)
        : moving
          ? "Moving: surveying as it goes"
          : "Idle: stop it somewhere to sample",
    ]);
  }
  rows.push(["Hex", `${s.q(s.tile[id])}, ${s.r(s.tile[id])}`]);
  rows.push(["Design", `${d.footprint ? `titan, ${d.footprint.tiles.length} hexes` : `size ${d.size}`} · range ${d.range} · sight ${d.sight}`]);
  // A titan's upkeep, each game minute, by its drive: unpaid, it stands and
  // holds fire.
  if (d.footprint) {
    const hexes = d.footprint.tiles.length;
    const power = powerOf(s.design[id]);
    const upkeep =
      power === "Electric"
        ? `${Math.ceil(hexes / 10)} batter${Math.ceil(hexes / 10) === 1 ? "y" : "ies"} a minute`
        : power === "Combustion"
          ? `${(5 * hexes).toLocaleString("en-US")} fuel a minute`
          : `${(5 * hexes).toLocaleString("en-US")} fuel a minute (combustion) or ${Math.ceil(hexes / 10)} batter${Math.ceil(hexes / 10) === 1 ? "y" : "ies"} (electric)`;
    rows.push([
      "Upkeep",
      <span data-tip="Paid each game minute; unpaid, it stands and holds fire">
        {upkeep}
        <Show when={isUnpowered(s.design[id])}>
          <span class="warn"> · unpaid: standing, holding fire</span>
        </Show>
      </span>,
    ]);
  }
  const armor = d.armor ?? 0;
  if (d.damage > 0) rows.push(["Weapon", `${d.damage} damage · penetrates ${d.pen ?? 0}`]);
  if (armor > 0) {
    const hurt = h.designs.filter((x) => x.damage > 0 && (x.pen ?? 0) >= armor).map((x) => x.name);
    rows.push(["Armour", `${armor}: hurt only by ${hurt.length ? hurt.join(", ").toLowerCase() : "nothing yet"}`]);
  }
  rows.push(["Id", `#${id} · key ${s.key[id].toString(16)}`]);
  return (
    <>
      <div class="inspector-title">Unit</div>
      {head}
      <Rows rows={rows} />
      <div class="tags">
        <For each={tags}>{([text, cls]) => <span class={`tag ${cls}`}>{text}</span>}</For>
      </div>
      <div class="actions">
        <button onClick={() => a.follow()}>{a.following() ? "Stop following" : "Follow"}</button>
        <button onClick={() => a.center(id)}>Centre</button>
        <button onClick={() => a.select(null)}>Deselect</button>
      </div>
    </>
  );
}

function TileView(p: Props & { pick: Pick }): JSX.Element {
  const { header: h, state: s, actions: a, pick } = p;
  const t = pick.tile!;
  const rows: Row[] = [["Terrain", TERRAIN_NAMES[s.statics.terrain[t]] ?? "?"]];
  if (s.statics.track[t]) rows.push(["Rail", "track"]);
  const corridor = s.corridor[t];
  if (corridor) rows.push(["Corridor", h.factions.filter((_, i) => corridor & (1 << i)).map((f) => f.name).join(", ")]);
  const at = s.depots.findIndex((d) => d.tile === t);
  const depot = at >= 0 ? s.depots[at] : undefined;
  // What it is, by name; its rounds when it stores them.
  const named = depot ? structureName(h, depot, at) : null;
  if (depot && depot.capacity) {
    const f = h.factions[depot.faction];
    rows.push(["Rounds", bar(depot.stock / depot.capacity, f?.color ?? "#ccc", `${int(depot.stock)} / ${int(depot.capacity)}`)]);
  }
  // A barracks: when it pays, its steel and whether it waits for more.
  const k = s.statics.producers.findIndex((pr) => pr.tile === t);
  if (k >= 0) {
    const b = s.barracks.get(k);
    const why = industry.barracksWhy(pick.q, pick.r);
    const has = b ? `${b.steel} steel${b.recruits === null ? "" : ` · ${b.recruits} recruits`}` : "free";
    rows.push([
      "Barracks",
      <span class="site-status">
        {has}
        <Show when={why?.[0] ?? (b?.waiting ? "Waiting" : null)}>{(w) => <span class="site-why-word">⚠ {w()}</span>}</Show>
        <Show when={why?.[1]}>{(d) => <span class="muted small">{d()}</span>}</Show>
      </span>,
    ]);
  }
  (h.sites ?? []).forEach((site, i) => {
    const st = s.sites[i];
    if (site.tile !== t || !st) return;
    const built = st.integrity / Math.max(1, site.full);
    const working = built * 100 >= site.operational_pct;
    const f = h.factions[site.faction];
    // A mine sharing ground with an older one of the same ore works less.
    const works = site.kind === "mine" ? mineWorks(h, i, s.width) : 19;
    const detail = site.kind === "mine" ? ` · ${int(st.reserve)}k left${works < 19 ? ` · works ${works} of 19 hexes (overlaps another mine)` : ""}` : "";
    // How built, then what it's making or why it isn't.
    const now = industry.status(i, s);
    rows.push([
      site.name,
      <span class="site-status">
        {bar(built, working ? (f?.color ?? "#ccc") : "#ff5242", `${Math.round(built * 100)}% built${detail}`)}
        <Show
          when={now?.why}
          fallback={
            <Show when={now?.note} fallback={<Show when={now?.rate != null}>{`Making ${int(now!.rate!)} ${now!.makes} a minute`}</Show>}>
              <span class="muted" data-tip={now!.detail}>
                {now!.note}
              </span>
            </Show>
          }
        >
          <span class="site-why-word">⚠ {now!.why}</span>
          <Show when={now!.detail}>
            <span class="muted small">{now!.detail}</span>
          </Show>
        </Show>
      </span>,
    ]);
  });
  // What it holds: each good a bar against the station's room, with
  // what's made and used of it a minute (a commander's own, from their
  // state; else this frame's freight).
  const depotIndex = s.depots.findIndex((d) => d.tile === t);
  const info = depotIndex >= 0 ? stationInfo(depotIndex) : undefined;
  const frameFreight = depotIndex >= 0 ? s.freight.get(depotIndex) : undefined;
  const held: [string, number][] = info
    ? [...info.freight].filter(([, n]) => n > 0)
    : frameFreight
      ? [...frameFreight].map((n, g) => [(h.freight_goods ?? [])[g], n] as [string, number]).filter(([, n]) => n > 0)
      : [];
  // Its trains: freight leaves on them (a station with none borrows one).
  if (info && info.trains !== null) {
    rows.push(["Trains", info.trains ? `${info.trains} here` : "none here: it borrows one from a station with two"]);
  }
  if (info || frameFreight) {
    const room = info?.capacity ?? Math.max(1, ...held.map(([, n]) => n));
    const hue = (g: string) => {
      const ore = g.replace(/ ore$/, "");
      const key = ORE_HUES.find(([k]) => k === ore || oreLabel(k) === ore)?.[0];
      const [r, gr, b] = key ? oreRGB(key) : [0.79, 0.76, 0.65];
      return `rgb(${Math.round(r * 255)},${Math.round(gr * 255)},${Math.round(b * 255)})`;
    };
    rows.push([
      "Holds",
      <Show when={held.length} fallback={"nothing"}>
        <span class="holdings" data-tip="Every station holds up to 50,000 of each good; home 1,500 of each (and 30,000 rounds). At the cap, what makes a good stops until trains take some away.">
          <For each={held}>
            {([g, n]) => {
              const f = info?.flow.get(g);
              return (
                <span class="holding">
                  <GoodIcon good={g} size={18} />
                  {bar(n / room, hue(g), `${g} ${int(n)}${info?.capacity ? ` / ${int(info.capacity)}` : ""}`)}
                  <Show when={f && (f.made || f.used)}>
                    <span class="holding-flow">
                      <Show when={f!.made}>
                        <span class="up">+{int(f!.made)}</span>{" "}
                      </Show>
                      <Show when={f!.used}>
                        <span class="down">−{int(f!.used)}</span>
                      </Show>
                    </span>
                  </Show>
                </span>
              );
            }}
          </For>
        </span>
      </Show>,
    ]);
  }
  const zone = h.world.zone_size;
  rows.push(["Zone", `${Math.floor(pick.q / zone)}, ${Math.floor(pick.r / zone)}`]);
  const ids = p.renderer.unitsOn(t);
  // A construction site here: what, how far along, and why it's stuck.
  const site = sites().find((x) => x.at[0] === pick.q && x.at[1] === pick.r);
  return (
    <>
      <div class="inspector-title">{site ? `${siteName(site)} (building)` : named ? `${named} · ${depot!.faction === 255 ? "decommissioning" : sideName(depot!.faction)}` : `Hex ${pick.q}, ${pick.r}`}</div>
      <Show when={site}>
        {(st) => (
          <div class="site-inspect">
            <Rows rows={[["Progress", bar(st().done / Math.max(1, st().need), "#c4512f", `${Math.floor((100 * st().done) / Math.max(1, st().need))}%`)]]} />
            <Show when={st().blocked} fallback={<p class="muted small">Your engineers are on it.</p>}>
              <p class="site-why">⚠ {BLOCKED[st().blocked!]?.[1]}</p>
            </Show>
            <div class="actions">
              <button data-tip="All the steel paid into it goes back home" onClick={() => cancelSites([st().at])}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </Show>
      <Rows rows={rows} />
      {/* A structure of yours you built: it can be taken down. */}
      <Show when={depot && depot.faction === a.commander() && BUILT_STEEL[depot.kind] !== undefined}>
        <div class="actions">
          <Decommission at={`${pick.q},${pick.r}`} refund={Math.floor((BUILT_STEEL[depot!.kind]! * 3) / 4)} run={() => a.decommission([pick.q, pick.r])} />
        </div>
      </Show>
      <Show when={ids.length} fallback={<p class="muted" style={{ "margin-top": "8px" }}>No units here</p>}>
        <div class="unit-list">
          <For each={ids}>
            {(id) => (
              <button onClick={() => a.select(id)}>
                <span class="swatch" style={{ background: h.factions[s.faction[id]]?.color, width: "9px", height: "9px", display: "inline-block" }} />
                {h.designs[s.design[id]].name}
                <span class="muted" style={{ "margin-left": "auto" }}>{`${s.hp[id]} hp · ${s.ammo[id]} ammo`}</span>
              </button>
            )}
          </For>
        </div>
      </Show>
    </>
  );
}
